import { FetchResult } from '@apollo/client';
import axios from 'axios';
import { useCallback } from 'react';

import { FileContext } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';

import {
  FileUploadError,
  FileUploadErrorCode,
  getSha256HashFromFile,
} from '../FileUploader.utils';
import { FileUploaderV2Props } from '../FileUploaderV2.types';

// The progress increments are used to calculate the progress of a single file upload.
const PROGRESS_INCREMENTS = {
  FILE_ACCEPTED: 5,
  FILE_CONTENTS_HASHED: 10,
  DOCUMENT_REGISTERED: 5,
} as const;
const PROGRESS_INCREMENTS_SUM = Object.values(PROGRESS_INCREMENTS).reduce(
  (acc, val) => acc + val,
  0
);
const REMAINING_PROGRESS_PERCENT_DECIMAL =
  (100 - PROGRESS_INCREMENTS_SUM) / 100;

export type UseUploadFileProps<Mutation> = Pick<
  FileUploaderV2Props<Mutation>,
  'generateSignedUploadURL' | 'context' | 'householdId'
> & {
  // When uploading a single file, we can set incremental progress values.
  setSingleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  // When uploading multiple files, we can increment the progress value.
  setMultipleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
};

export interface UploadedFileData<Mutation> {
  file: File | null;
  result: FetchResult<Mutation> | null;
  householdId: string | null;
}

export function useUploadFile<Mutation>({
  generateSignedUploadURL,
  setSingleFileUploadProgress,
  setMultipleFileUploadProgress,
  context = FileContext.Household,
  householdId,
}: UseUploadFileProps<Mutation>) {
  const logUploadError = useCallback(
    (error: Error, file: File) => {
      diagnostics.error('File upload failed', error, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        householdId,
        context,
      });
    },
    [householdId, context]
  );

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedFileData<Mutation>> => {
      if (!generateSignedUploadURL) {
        throw new FileUploadError(
          FileUploadErrorCode.RegistrationFailed,
          new Error(
            'generateSignedUploadURL is required for legacy upload flow'
          )
        );
      }

      if (file.size === 0) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(FileUploadErrorCode.EmptyFile);
        throw error;
      }

      setSingleFileUploadProgress(PROGRESS_INCREMENTS.FILE_ACCEPTED);

      let hash: string;
      try {
        hash = await getSha256HashFromFile(file);
        setSingleFileUploadProgress(
          (lastValue) =>
            (lastValue || 0) + PROGRESS_INCREMENTS.FILE_CONTENTS_HASHED
        );
      } catch (err) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.HashFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }

      let signedUploadURL: string;
      let mutationResult: Omit<
        Awaited<ReturnType<typeof generateSignedUploadURL>>,
        'signedUploadURL'
      >;
      try {
        const result = await generateSignedUploadURL(
          { hash, contentType: file.type, context, householdId },
          {
            onError: (err) => {
              setSingleFileUploadProgress(0);
              throw err;
            },
            onCompleted: () =>
              setSingleFileUploadProgress(
                (lastValue) =>
                  lastValue + PROGRESS_INCREMENTS.DOCUMENT_REGISTERED
              ),
          }
        );
        signedUploadURL = result.signedUploadURL;
        mutationResult = { data: result.data, errors: result.errors };
      } catch (err) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.RegistrationFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }

      if (!mutationResult.data) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.RegistrationFailed
        );
        logUploadError(error, file);
        throw error;
      }

      try {
        await axios(signedUploadURL, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
            'X-Amz-Content-Sha256': hash,
          },
          data: file,
          onUploadProgress: (progressEvent) => {
            if (typeof progressEvent.progress === 'undefined') return;
            const nextProgressValue =
              PROGRESS_INCREMENTS_SUM +
              REMAINING_PROGRESS_PERCENT_DECIMAL *
                (progressEvent.progress * 100);
            setSingleFileUploadProgress(nextProgressValue);
          },
        });
      } catch (err) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.UploadFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }

      setMultipleFileUploadProgress((lastValue) => lastValue + 1);
      return { file, result: mutationResult, householdId };
    },
    [
      setSingleFileUploadProgress,
      generateSignedUploadURL,
      context,
      householdId,
      setMultipleFileUploadProgress,
      logUploadError,
    ]
  );

  return {
    uploadFile,
  };
}
